import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../assets/logoS.png";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = ["Services", "About", "Equipment", "Treatments", "Contact"];

  return (
    <>
      <Nav
        as={motion.nav}
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        $scrolled={scrolled}
      >
        <LogoWrap href="/">
          <img src={logo} alt="Elaia Aesthetic" />
        </LogoWrap>

        <Links>
          {links.map((link) => (
            <NavLink key={link} href={`#${link.toLowerCase()}`}>
              <span>{link}</span>
              <Line />
            </NavLink>
          ))}
          <BookBtn
            as={motion.a}
            href="#contact"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            Book Now
          </BookBtn>
        </Links>

        <Burger onClick={() => setMenuOpen(!menuOpen)} $open={menuOpen}>
          <span />
          <span />
          <span />
        </Burger>
      </Nav>

      <AnimatePresence>
        {menuOpen && (
          <MobileMenu
            as={motion.div}
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            {links.map((link, i) => (
              <MobileLink
                key={link}
                href={`#${link.toLowerCase()}`}
                as={motion.a}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.07 + 0.2 }}
                onClick={() => setMenuOpen(false)}
              >
                {link}
              </MobileLink>
            ))}
            <MobileBook href="#contact" onClick={() => setMenuOpen(false)}>
              Book Now
            </MobileBook>
          </MobileMenu>
        )}
      </AnimatePresence>
    </>
  );
};

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ $scrolled }) => ($scrolled ? "14px 10%" : "28px 10%")};
  background: ${({ $scrolled }) =>
    $scrolled ? "rgba(228, 212, 197, 0.92)" : "transparent"};
  backdrop-filter: ${({ $scrolled }) => ($scrolled ? "blur(20px)" : "none")};
  border-bottom: ${({ $scrolled }) =>
    $scrolled ? "1px solid rgba(196, 149, 122, 0.15)" : "none"};
  transition: all 0.5s cubic-bezier(0.22, 1, 0.36, 1);

  @media (max-width: 768px) {
    padding: ${({ $scrolled }) => ($scrolled ? "14px 24px" : "22px 24px")};
  }
`;

const LogoWrap = styled.a`
  display: flex;
  align-items: center;
  text-decoration: none;
  img {
    height: 40px;
    filter: brightness(0.6);
    /* width: auto; */
  }
`;

const Links = styled.div`
  display: flex;
  align-items: center;
  gap: 36px;
  @media (max-width: 900px) {
    display: none;
  }
`;

const NavLink = styled.a`
  position: relative;
  text-decoration: none;
  color: var(--cream);
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  padding-bottom: 4px;
  overflow: hidden;
  &:hover span {
    color: var(--rose);
  }
  &:hover div {
    transform: scaleX(1);
  }
  span {
    transition: color 0.3s;
  }
`;

const Line = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 1px;
  background: var(--rose);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
`;

const BookBtn = styled.a`
  text-decoration: none;
  color: var(--charcoal);
  background: var(--rose);
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 12px 28px;
  transition: background 0.3s;
  &:hover {
    background: var(--gold);
  }
`;

const Burger = styled.button`
  display: none;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  span {
    display: block;
    width: 26px;
    height: 1px;
    background: var(--cream);
    transition: all 0.3s;
    transform-origin: center;
  }
  ${({ $open }) =>
    $open &&
    `
    span:nth-child(1) { transform: translateY(6px) rotate(45deg); }
    span:nth-child(2) { opacity: 0; }
    span:nth-child(3) { transform: translateY(-6px) rotate(-45deg); }
  `}
  @media (max-width: 900px) {
    display: flex;
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999;
  background: var(--charcoal);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 36px;
`;

const MobileLink = styled.a`
  text-decoration: none;
  color: var(--cream);
  font-family: var(--font-display);
  font-size: clamp(32px, 8vw, 52px);
  font-weight: 300;
  letter-spacing: 0.05em;
  &:hover {
    color: var(--rose);
  }
`;

const MobileBook = styled.a`
  text-decoration: none;
  color: var(--charcoal);
  background: var(--rose);
  font-family: var(--font-body);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 14px 40px;
  margin-top: 16px;
`;

export default Navbar;
